import Vue from 'vue';
import Router from 'vue-router';
import config from '@/config';
import store from '@/store/store';
import {
  refreshToken,
  getDistributorErpConfig,
  getValidMerchant,
  getAccessToken,
  getTokenByListing,
  getQueryMenu,
  getSinglePageRoleCommon,
  getStoreDomainName,
} from '@/utils/user';
import {getToken} from "@/utils/cookie";
import {
  getCurrencyData,
  deleteTicket,
  getShopDomain,
  getDeliveryAreasAllData,
  getConfigurationItemData,
  showPageInitFailureModal,
  isInterceptorHandled,
  resetInterceptorHandled,
  isChunkLoadError,
} from "@/utils/common";
import {clearAllLoading} from '@/utils/loading';

const originalPush = Router.prototype.push;
Router.prototype.push = function push(location) {
  return originalPush.call(this, location).catch(err => err);
};
Vue.use(Router);
let countTime = config.curTime;
const routes = [
  {
    path: '/',
    redirect: '/index',
    component: () => import('@/components/layout/shopping/main'), // 引入主文件
    children: [
      {
        path: '/index', // 商城--首页
        component: () => import('@/views/shopping/index/components/index')
      },
      {
        path: '/shopList', // 商城--商品列表页
        component: () => import('@/views/shopping/common/shopList')
      },
      {
        path: '/shopDetails/:id', // 商城--商品列表详情页
        component: () => import('@/views/shopping/index/components/shopDetails'),
        name: '/shopDetails'
      },
      {
        path: '/shoppingCartList', // 商城--购物车
        component: () => import('@/views/shopping/index/components/shoppingCartList'),
      },
      {
        path: '/mallOrderSubmission', // 商城--购物车--订单提交
        component: () => import('@/views/shopping/index/components/mallOrderSubmission'),
      },
      {
        path: '/legalDeclaration', // 法律声明
        component: () => import('@/views/shopping/index/components/legalDeclaration')
      },
      {
        path: '/privacyPolicy', // 隐私政策
        component: () => import('@/views/shopping/index/components/privacyPolicy')
      },
      {
        path: '/termsOfService', // 服务条款
        component: () => import('@/views/shopping/index/components/termsOfService')
      },
      {
        path: '/introduce', // YMS商城介绍
        component: () => import('@/views/shopping/index/components/introduce')
      },
      {
        path: '/businessIntroduce', // 平台业务介绍
        component: () => import('@/views/shopping/index/components/businessIntroduce')
      },
      {
        path: '/contactUs', // 平台业务介绍
        component: () => import('@/views/shopping/index/components/contactUs')
      },
      /*================================泰瑞森相关介绍内容跳转=============================*/
      {
        path: '/trsAboutTalison', // 泰瑞森介绍
        component: () => import('@/views/shopping/index/components/trsAboutTalison.vue')
      },
      {
        path: '/trsContact', // 泰瑞森联系
        component: () => import('@/views/shopping/index/components/trsContact.vue')
      },
      {
        path: '/trsPrivacyPolicy', // 泰瑞森隐私政策
        component: () => import('@/views/shopping/index/components/trsPrivacyPolicy.vue')
      },
      {
        path: '/trsReturnPolicy', // 泰瑞森退货政策
        component: () => import('@/views/shopping/index/components/trsReturnPolicy.vue')
      },
      {
        path: '/trsShippingPolicy', // 泰瑞森运输政策
        component: () => import('@/views/shopping/index/components/trsShippingPolicy.vue')
      },
      {
        path: '/trsTermsService', // 泰瑞森服务条款
        component: () => import('@/views/shopping/index/components/trsTermsService.vue')
      },
    ]
  },
  {
    path: '/help', // 商城--首页
    component: () => import('@/views/shopping/index/components/help')
  },
  {
    path: '/supplierEntryGuidelines', // 商城--供应商入驻指南
    component: () => import('@/views/shopping/index/components/supplierEntryGuidelines')
  },
  {
    path: '*',
    redirect: '/index'
  }
];
const router = new Router({
  mode: 'hash',
  routes
});
/**
 * 初始化商城语种和配置项缓存。
 * @returns {Promise<void>}
 */
const initLangAndConfig = async () => {
  const lang = getStoreDomainName();
  if (lang) {
    localStorage.setItem('lang', lang);
    Vue.config.lang = lang;
  }
  await getConfigurationItemData();
};

/**
 * 统一处理商城首页初始化失败场景。
 * @param {Function} next vue-router 导航回调。
 * @param {Error} error 当前初始化异常。
 */
const handlePageInitFailure = (next, error) => {
  console.error('router beforeEach error:', error);
  if (isChunkLoadError(error)) {
    window.location.reload();
    return;
  }
  next(false);
  if (isInterceptorHandled) {
    resetInterceptorHandled();
    return;
  }
  showPageInitFailureModal();
}

router.beforeEach(async (to, from, next) => {
  localStorage.setItem('system', 'shopping');
  const localUrl = window.location.href;
  const token = getToken();
  try {
    /* ========== 1. listing 来源 ========= */
    if (localUrl.includes('from=listing')) {
      store.commit('fromListing', true);
      await getTokenByListing();
      const [erpConfig, deliveryData, shopDomain] = await Promise.all([
        getDistributorErpConfig(),
        getDeliveryAreasAllData('listing'),
        getShopDomain()
      ]);
      if (!erpConfig) {
        throw new Error('getDistributorErpConfig failed');
      }
      if (shopDomain === false) {
        throw new Error('getShopDomain failed');
      }
      if (deliveryData) {
        await initLangAndConfig();
        clearAllLoading();
        return next();
      }
      throw new Error('getDeliveryAreasAllData failed');
    }
    /* ========== 2. ticket 登录回调 ========= */
    if (localUrl.includes('ticket')) {
      try {
        await getAccessToken(localUrl, true);
      } finally {
        clearAllLoading();
        window.location.href = deleteTicket(localUrl);
        window.location.reload();
      }
      return;
    }
    /* ========== 3. 正常进入系统 ========= */
    refreshToken(countTime, true);
    if (token) {
      const menu = await getQueryMenu('yms-distributor-service');
      if (menu === null) {
        clearAllLoading();
        resetInterceptorHandled();
        return next(false);
      }
      if (menu === false) {
        throw new Error('getQueryMenu failed');
      }
      const [erpConfig, shopDomain, role] = await Promise.all([
        getDistributorErpConfig(),
        getShopDomain(),
        getSinglePageRoleCommon('shop_shopList', 'yms-distributor-service'),
        getCurrencyData()
      ]);
      if (erpConfig === false) {
        throw new Error('getDistributorErpConfig failed');
      }
      if (shopDomain === false) {
        throw new Error('getShopDomain failed');
      }
      if (role === false) {
        throw new Error('getSinglePageRoleCommon failed');
      }
      if (erpConfig && shopDomain) {
        const value = await getValidMerchant('shopping');
        if (!value) {
          return next(false);
        }
        await initLangAndConfig();
        clearAllLoading();
        return next();
      }
    } else {
      const [erpConfig, shopDomain] = await Promise.all([
        getDistributorErpConfig(),
        getShopDomain()
      ]);
      if (erpConfig === false) {
        throw new Error('getDistributorErpConfig failed');
      }
      if (shopDomain === false) {
        throw new Error('getShopDomain failed');
      }
      if (erpConfig) {
        await initLangAndConfig();
        clearAllLoading();
        return next();
      }
    }
  } catch (e) {
    handlePageInitFailure(next, e);
  }
});
// 路由错误处理
router.onError((error) => {
  if (isChunkLoadError(error)) {
    window.location.reload();
    return;
  }
  console.error('router error:', error);
});

export default router;
